import { useInfiniteQuery, useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { api, unwrap } from '@/lib/api'
import type { AppNotification, DataEnvelope, NotificationChannel, NotificationDigest, NotificationPreference, Paginated } from '@/types/api'

export const notificationKeys = {
  all: ['notifications'] as const,
  list: (unread: boolean) => ['notifications', 'list', { unread }] as const,
  unread: () => ['notifications', 'unread-count'] as const,
  preferences: () => ['notifications', 'preferences'] as const,
}

/** Polls the unread count (cheap endpoint) every 30 s while the tab is visible. */
export function useUnreadCount() {
  return useQuery({
    queryKey: notificationKeys.unread(),
    queryFn: ({ signal }) => unwrap(api.get<DataEnvelope<{ count: number }>>('/notifications/unread-count', { signal, silent: true })).then((d) => d.count),
    refetchInterval: 30_000,
    refetchIntervalInBackground: false,
  })
}

export function useNotifications(unread = false, enabled = true) {
  return useInfiniteQuery({
    enabled,
    queryKey: notificationKeys.list(unread),
    queryFn: ({ pageParam, signal }) =>
      api.get<Paginated<AppNotification>>('/notifications', { signal, query: { unread: unread ? 1 : undefined, cursor: pageParam, per_page: 20 } }),
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (last) => last.meta?.next_cursor ?? undefined,
  })
}

export function useMarkRead() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (id: string | 'all') => (id === 'all' ? api.post<void>('/notifications/read-all') : api.post<void>(`/notifications/${id}/read`)),
    onSettled: () => void qc.invalidateQueries({ queryKey: notificationKeys.all }),
  })
}

export function useNotificationPreferences() {
  return useQuery({
    queryKey: notificationKeys.preferences(),
    queryFn: ({ signal }) => unwrap(api.get<DataEnvelope<NotificationPreference[]>>('/notification-preferences', { signal })),
  })
}

export interface PreferenceChange {
  event_type: string
  channels?: Partial<Record<NotificationChannel, boolean>>
  digest?: NotificationDigest
}

export function useUpdatePreferences() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (preferences: PreferenceChange[]) => unwrap(api.put<DataEnvelope<NotificationPreference[]>>('/notification-preferences', { preferences })),
    onSuccess: (prefs) => qc.setQueryData(notificationKeys.preferences(), prefs),
  })
}
